import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Layout } from '../../components/Layout';

interface SequenceStep {
  stepNumber: number;
  daysAfterPrevious: number;
  subject: string;
  body: string;
}

interface Template {
  id: string;
  name: string;
  updatedAt: any;
  sequenceSteps: SequenceStep[];
}

interface ImportResult {
  campaignId: string;
  contactsCreated: number;
  contactsSkipped: number;
  skipReasons: string[];
}

const DEFAULT_STEPS: SequenceStep[] = [
  { stepNumber: 1, daysAfterPrevious: 0,  subject: '', body: '' },
  { stepNumber: 2, daysAfterPrevious: 5,  subject: '', body: '' },
  { stepNumber: 3, daysAfterPrevious: 5,  subject: '', body: '' },
  { stepNumber: 4, daysAfterPrevious: 5,  subject: '', body: '' },
];

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

export function NewCampaignPage() {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [campaignName, setCampaignName] = useState('');
  const [fileName, setFileName] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);

  // Template library
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Step editor
  const [steps, setSteps] = useState<SequenceStep[]>(DEFAULT_STEPS);

  useEffect(() => {
    getFunctions(); // warm
    httpsCallable<void, { templates: Template[] }>(getFunctions(), 'listOutreachTemplates')()
      .then(({ data }) => setTemplates(data.templates))
      .catch(() => {/* non-fatal */});
  }, []);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) { setSteps(DEFAULT_STEPS); return; }
    const tpl = templates.find(t => t.id === templateId);
    if (tpl?.sequenceSteps?.length) setSteps(tpl.sequenceSteps);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setCsvFile(file);
    setFileName(file?.name ?? '');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!campaignName.trim()) { setError('Campaign name is required.'); return; }
    if (!csvFile) { setError('Please select a CSV file.'); return; }

    setLoading(true);
    try {
      const csvText = await readFileAsText(csvFile);
      const createFn = httpsCallable<any, ImportResult>(getFunctions(), 'createCollegeOutreachCampaign');
      const { data } = await createFn({ campaignName: campaignName.trim(), csvText });

      const hasContent = steps.some(s => s.subject.trim() || s.body.trim());
      if (hasContent) {
        await httpsCallable(getFunctions(), 'updateCollegeOutreachTemplates')(
          { campaignId: data.campaignId, steps }
        );
      }

      setResult(data);
    } catch (e: any) {
      const raw = e?.message || String(e);
      setError(raw.replace(/^Firebase: /, '').replace(/ \(functions\/[^)]+\)\.?$/, ''));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <Layout>
        <button className="back-btn" onClick={() => navigate('/outreach')}>← Campaigns</button>
        <div style={{ maxWidth: 560 }}>
          <h1 className="page-title" style={{ marginBottom: 8 }}>Campaign created</h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 24, fontSize: 14 }}>
            Status is <strong>draft</strong> — no emails will send until you activate the campaign.
          </p>

          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: result.contactsSkipped > 0 ? '1fr 1fr' : '1fr', gap: 20 }}>
              <div>
                <div className="stat-label">Contacts imported</div>
                <div className="stat-value" style={{ fontSize: 26, marginTop: 4 }}>
                  {result.contactsCreated.toLocaleString()}
                </div>
              </div>
              {result.contactsSkipped > 0 && (
                <div>
                  <div className="stat-label">Rows skipped</div>
                  <div className="stat-value" style={{ fontSize: 26, marginTop: 4, color: 'var(--warning)' }}>
                    {result.contactsSkipped.toLocaleString()}
                  </div>
                </div>
              )}
            </div>

            {result.skipReasons.length > 0 && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
                }}>
                  Skip reasons
                </div>
                <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                  {result.skipReasons.map((r, i) => (
                    <div key={i} style={{
                      fontSize: 12, color: 'var(--text-muted)',
                      fontFamily: 'monospace', padding: '2px 0',
                    }}>
                      {r}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-primary" onClick={() => navigate(`/outreach/${result.campaignId}`)}>
              View campaign →
            </button>
            <button className="btn-secondary" onClick={() => navigate('/outreach')}>
              Back to campaigns
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <button className="back-btn" onClick={() => navigate('/outreach')}>← Campaigns</button>
      <div style={{ maxWidth: 640 }}>
        <h1 className="page-title" style={{ marginBottom: 24 }}>New Campaign</h1>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="form-group">
            <label className="form-label">Campaign Name</label>
            <input
              className="form-input"
              type="text"
              placeholder="e.g. Fall 2026 — State Universities"
              value={campaignName}
              onChange={e => setCampaignName(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Contact List (CSV)</label>
            <div
              style={{
                border: `1px dashed ${fileName ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-md)',
                padding: '20px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                background: fileName ? 'var(--accent-tint)' : 'var(--bg-secondary)',
                transition: 'background 0.15s, border-color 0.15s',
              }}
              onClick={() => fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              {fileName ? (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--accent-text)' }}>{fileName}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Click to change</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>
                    Click to upload a CSV file
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7 }}>
                    Required: <code>email</code>, <code>school</code> — <code>firstName</code> and <code>segment</code> are optional
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Sequence templates ─────────────────────────────────────────────── */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <label className="form-label" style={{ margin: 0 }}>Sequence Templates</label>
              {templates.length > 0 && (
                <select
                  className="form-input"
                  value={selectedTemplateId}
                  onChange={e => handleTemplateSelect(e.target.value)}
                  style={{ width: 'auto', fontSize: 13, padding: '5px 10px' }}
                >
                  <option value="">Start from template…</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {steps.map((step, idx) => (
                <div
                  key={step.stepNumber}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    padding: 14,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, flexShrink: 0 }}>Step {step.stepNumber}</span>
                    {idx === 0 ? (
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sends immediately on activation</span>
                    ) : (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                        <input
                          type="number"
                          min={1}
                          className="form-input"
                          value={step.daysAfterPrevious}
                          onChange={e => setSteps(prev => prev.map((s, i) =>
                            i === idx ? { ...s, daysAfterPrevious: Math.max(1, parseInt(e.target.value, 10) || 1) } : s
                          ))}
                          style={{ width: 52, padding: '2px 6px', fontSize: 12, textAlign: 'center' }}
                        />
                        days after previous
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => setSteps(prev =>
                        prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepNumber: i + 1 }))
                      )}
                      disabled={steps.length === 1}
                      style={{
                        marginLeft: 'auto', fontSize: 12, color: 'var(--error)',
                        background: 'none', border: 'none', padding: '2px 6px',
                        cursor: steps.length === 1 ? 'not-allowed' : 'pointer',
                        opacity: steps.length === 1 ? 0.35 : 1,
                      }}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label className="form-label">Subject</label>
                    <input
                      className="form-input"
                      type="text"
                      placeholder="Email subject line"
                      value={step.subject}
                      onChange={e => setSteps(prev => prev.map((s, i) =>
                        i === idx ? { ...s, subject: e.target.value } : s
                      ))}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Body</label>
                    <textarea
                      className="form-input"
                      placeholder={`Email body — use {{firstName}}, {{school}} for merge tags`}
                      value={step.body}
                      rows={5}
                      onChange={e => setSteps(prev => prev.map((s, i) =>
                        i === idx ? { ...s, body: e.target.value } : s
                      ))}
                      style={{ resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setSteps(prev => [
                ...prev,
                { stepNumber: prev.length + 1, daysAfterPrevious: 5, subject: '', body: '' },
              ])}
              style={{ marginTop: 4, fontSize: 13, padding: '7px 16px' }}
            >
              + Add Step
            </button>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              Templates are optional — you can also fill them in on the campaign detail page after creation.
            </p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div>
            <button
              className="btn-primary"
              type="submit"
              disabled={loading || !campaignName.trim() || !csvFile}
            >
              {loading ? 'Creating…' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
